export interface InactivityTimerOptions {
  timeoutMs: number;
  onTimeout: () => void;
}

export interface InactivityTimer {
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export function createInactivityTimer(
  opts: InactivityTimerOptions,
): InactivityTimer {
  let id: ReturnType<typeof setTimeout> | null = null;
  function clear(): void {
    if (id !== null) {
      clearTimeout(id);
      id = null;
    }
  }
  function schedule(): void {
    clear();
    id = setTimeout(() => {
      id = null;
      opts.onTimeout();
    }, opts.timeoutMs);
  }
  return {
    start: () => schedule(),
    stop: () => clear(),
    reset: () => schedule(),
  };
}

// Cross-tab lock broadcast. One tab timing out (or user clicking
// "lock now") should propagate to peer tabs so a stale UI doesn't
// sit on a still-unlocked-looking screen after the worker was
// locked out from under it. BroadcastChannel in evergreen browsers;
// `storage` event fallback where BroadcastChannel is missing (both
// paths carry only a millisecond timestamp under a known key —
// no secret material).

export interface LockBroadcast {
  announce: () => void;
  subscribe: (cb: () => void) => () => void;
  close: () => void;
}

const STORAGE_KEY_PREFIX = "vaultwisp:lockedAt:";

export function createLockBroadcast(channelName: string): LockBroadcast {
  if (typeof BroadcastChannel !== "undefined") {
    const ch = new BroadcastChannel(channelName);
    return {
      announce: () => ch.postMessage({ t: Date.now() }),
      subscribe: (cb) => {
        const handler = () => cb();
        ch.addEventListener("message", handler);
        return () => ch.removeEventListener("message", handler);
      },
      close: () => ch.close(),
    };
  }
  // storage-event fallback: writes to localStorage fire `storage`
  // events in *other* tabs of the same origin, not the writer.
  const key = STORAGE_KEY_PREFIX + channelName;
  return {
    announce: () => {
      try {
        window.localStorage.setItem(key, String(Date.now()));
      } catch {
        // Private-mode or quota — degrade silently. Local lock still fires.
      }
    },
    subscribe: (cb) => {
      const handler = (e: StorageEvent) => {
        if (e.key === key && e.newValue !== null) cb();
      };
      window.addEventListener("storage", handler);
      return () => window.removeEventListener("storage", handler);
    },
    close: () => {
      // Nothing to close; storage events are global listeners.
    },
  };
}

// Visibility-based early-lock. Closes the bypass where a backgrounded
// tab's setTimeout is throttled past the configured auto-lock
// timeout. On hidden, we record the wall-clock instant; on visible,
// we compare elapsed against the timeout. Hidden ≥ timeout → lock
// immediately. Hidden < timeout → caller resets the normal timer.

export interface VisibilityLockGuardOptions {
  timeoutMs: number;
  onLock: () => void;
  onVisible: () => void;
  now?: () => number;
}

export interface VisibilityLockGuard {
  stop: () => void;
}

export function createVisibilityLockGuard(
  opts: VisibilityLockGuardOptions,
): VisibilityLockGuard {
  const now = opts.now ?? (() => Date.now());
  let hiddenAt: number | null = null;
  const handler = () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = now();
      return;
    }
    if (document.visibilityState === "visible") {
      if (hiddenAt !== null && now() - hiddenAt >= opts.timeoutMs) {
        hiddenAt = null;
        opts.onLock();
        return;
      }
      hiddenAt = null;
      opts.onVisible();
    }
  };
  document.addEventListener("visibilitychange", handler);
  return {
    stop: () => document.removeEventListener("visibilitychange", handler),
  };
}
